import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";

import { DevicesActions } from "../_actions/DevicesActions";
import { genericActions } from "../_helpers/ActionErrorWrapper";
import { ContentSpace, MainPageWithButtons, CapsuleButton, WaitSpinner } from "../_components";
import { CustomSearchBar } from "../../Common/_components/CustomSearchBar";
import { DeviceSearchResultsTable } from "./DeviceSearchResultsTable";

const DEVICE_NOT_EXIST_ERROR_CODE = "203";

export function DeviceSearchModule() {

    //LIBRARIES
    const dispatch = useDispatch();
    const { t } = useTranslation();

    //LOCAL STATE
    const [bluetoothId, setBluetoothId] = useState("");
    const [devices, setDevices] = useState(null);
    const [isSearching, setIsSearching] = useState(false);
    const [notFound, setNotFound] = useState(false);

    //ACTION HANDLERS
    async function handleSearch() {
        const trimmedId = bluetoothId.trim();
        if (!trimmedId || isSearching)
            return;

        setIsSearching(true);
        setNotFound(false);
        setDevices(null);
        try {
            // The generic wrapper resolves to undefined on REQUEST_EXPIRED;
            // keep the "no search yet" state instead of rendering a table.
            const foundDevices = await dispatch(DevicesActions.getDeviceByBluetoothId(trimmedId));
            setDevices(foundDevices ?? null);
        } catch (error) {
            if (String(error.name) === DEVICE_NOT_EXIST_ERROR_CODE) {
                setNotFound(true);
            } else {
                dispatch(genericActions.errorCatch(error));
            }
        } finally {
            setIsSearching(false);
        }
    }

    function handleKeyDown(e) {
        if (e.key === "Enter")
            handleSearch();
    }

    //VIEW
    return (
        <ContentSpace
            mainContent={
                <MainPageWithButtons
                    pageTitle={t("mod_device_search_title")}
                    content={content()}
                    size={'X_LARGE'}
                />
            }
        />
    );

    function content() {
        return (
            <div className="w-100">
                <div className="d-flex align-items-center mb-4" onKeyDown={handleKeyDown}>
                    <CustomSearchBar
                        value={bluetoothId}
                        onChange={e => setBluetoothId(e.target.value)}
                        placeholder={t("mod_device_search_placeholder")}
                        className="flex-grow-1 mr-3"
                    />
                    <CapsuleButton
                        onClick={handleSearch}
                        text={t("mod_device_search_button")}
                        isEnabled={bluetoothId.trim() !== "" && !isSearching}
                    />
                </div>

                {searchResult()}
            </div>
        );
    }

    function searchResult() {
        if (isSearching)
            return <WaitSpinner />;

        if (notFound)
            return <div className="alert alert-warning">{t("global_device_not_found")}</div>;

        if (devices == null)
            return null;

        return <DeviceSearchResultsTable devices={devices} />;
    }
}